import { lookupBalance } from './balanceLookup';

/**
 * calcDH — Double Hung cut list calculations
 *
 * CRITICAL: Field names must match WindowRow.jsx exactly.
 * WindowRow stores glass fields with prefix: "Keeper" or "Lock"
 *   win.KeeperGlassPkg   win.KeeperGlassOpts
 *   win.KeeperGridType   win.KeeperGridPat   win.KeeperGridV   win.KeeperGridH
 *   win.LockGlassPkg     win.LockGlassOpts
 *   win.LockGridType     win.LockGridPat     win.LockGridV     win.LockGridH
 *
 * Top-level fields from WindowRow:
 *   overallW (from win.width), height (from win.height)
 *   config, color, finType, screenType, screenSize, meshType
 *   gasFill, qty, label
 */
export function calcDH(win) {
  const {
    // ── Dimensions ──────────────────────────────────────────────────────────
    width: overallW,
    height,
    // ── Identity ────────────────────────────────────────────────────────────
    config    = 'Single',
    color     = 'White',
    finType   = 'Without Fin',
    qty       = 1,
    label     = '',
    // ── Screen ──────────────────────────────────────────────────────────────
    screenType = 'None',
    screenSize = 'Full Scrn',
    meshType   = 'STD Mesh',
    // ── Gas ─────────────────────────────────────────────────────────────────
    gasFill    = 'Argon',
    // ── Sash Split — default 50/50; rare asymmetric custom DH builds ────────
    sashSplit  = '50/50',
    // ── Keeper Glass — prefix "Keeper" ──────────────────────────────────────
    KeeperGlassPkg  = 'DLA',
    KeeperGlassOpts = 'None',
    KeeperGridType  = 'None',   // "Flat Grids", "Contour Grids", "None"
    KeeperGridPat   = 'None',   // "Colonial", "Standard Prairie", "Spanned Prairie", "None"
    KeeperGridV     = '0',
    KeeperGridH     = '0',
    // ── Lock Glass — prefix "Lock" ──────────────────────────────────────────
    LockGlassPkg    = 'DLA',
    LockGlassOpts   = 'None',
    LockGridType    = 'None',
    LockGridPat     = 'None',
    LockGridV       = '0',
    LockGridH       = '0',
  } = win;

  // ── Color helpers ─────────────────────────────────────────────────────────
  const colorLower = (color || '').toLowerCase();
  const isDark     = colorLower.includes('woodgrain');
  const isWhite    = color.toUpperCase() === 'WHITE';
  const withFin    = finType === 'With Fin' || finType === 'With Fin & J';

  const glazbeadColor = isDark
    ? (colorLower.includes('white') ? 'White' : 'Clay')
    : color;

  // ── Grid color helper — matches INSIDE-visible frame color ──────────────
  // Frame naming convention: "Inside/Outside" (e.g. "Dk Woodgrain/White")
  //   - Dk Woodgrain/White → inside is Dk Woodgrain, outside is White
  //   - Woodgrain          → all Woodgrain (fully wrapped)
  //   - White              → all White
  // Grid outside face is ALWAYS White (matches exterior). Grid inside face
  // matches the inside frame color. We report the INSIDE color to Tracy Corners
  // so they know how to build the grid.
  const insideGridColor = color.includes('/')
    ? color.split('/')[0].trim()   // e.g. 'Dk Woodgrain/White' → 'Dk Woodgrain'
    : color;


  // ── Lite multiplier ───────────────────────────────────────────────────────
  const liteMap  = { Single: 1, '2-Lite': 2, '3-Lite': 3, '4-Lite': 4, '5-Lite': 5, 'Q/H/Q': 4 };
  const numLites = liteMap[config] || 1;
  const indivW   = overallW / numLites;
  const unitQty  = qty * numLites;

  // ── Sash Split Ratios ─────────────────────────────────────────────────────
  // sashSplit string ('50/50', '25/75', '75/25') → upperRatio (top/Keeper)
  // and lowerRatio (bottom/Lock). Default 50/50 keeps existing windows identical.
  const [upperPct, lowerPct] = (sashSplit || '50/50').split('/').map(Number);
  const upperRatio = upperPct / 100;
  const lowerRatio = lowerPct / 100;

  // ── Spring height: (height/2) - 0.5 ──────────────────────────────────────
  const springH = height / 2 - 0.5;

  // ── L Sash reference ──────────────────────────────────────────────────────
  const lSashW = indivW - (isDark ? 3.5625 : 3.5);
  const lSashH = height * lowerRatio - 0.6875;  // bottom (Lock) sash

  // ── Glass sizes ───────────────────────────────────────────────────────────
  // ╔═══════════════════════════════════════════════════════════════════════╗
  // ║  GLASS SIZE ADJUSTMENT — added 2026-05-14 (Kwizera request)            ║
  // ║  DH glass only: width +1/16", height -1/16" — Keeper AND Lock.         ║
  // ║  Nothing else (sash, frame, glazbead, screen) is affected.            ║
  // ║  TO REVERT: remove the "+ 0.0625" / "- 0.0625" tweaks below and        ║
  // ║  the base formulas return to original. Grep: GLASS SIZE ADJUSTMENT     ║
  // ╚═══════════════════════════════════════════════════════════════════════╝
  const GLASS_W_ADJ =  0.0625;  // +1/16" — GLASS SIZE ADJUSTMENT
  const GLASS_H_ADJ = -0.0625;  // -1/16" — GLASS SIZE ADJUSTMENT
  const keeperGlassW = indivW - (isDark ? 6.625  : 6.5625) + GLASS_W_ADJ;
  const keeperGlassH = height * upperRatio - 3.625 + GLASS_H_ADJ;
  const lockGlassW   = indivW - (isDark ? 5.625  : 5.5625) + GLASS_W_ADJ;
  const lockGlassH   = height * lowerRatio - 2.625 + GLASS_H_ADJ;

  // ── Grid helper — parse grid fields for one pane ──────────────────────────
  // Returns: { hasGrids, gridType, gridPattern, gridQtyV, gridQtyH,
  //            gridLenH, gridLenV, horizQty, vertQty, gridColor }
  function parseGrid(gridType, gridPat, gridV, gridH, glassW, glassH) {
    const qV = parseInt(gridV) || 0;
    const qH = parseInt(gridH) || 0;
    const has = !!gridType
             && gridType !== 'None'
             && gridType !== ''
             && (qV > 0 || qH > 0);

    return {
      hasGrids:    has,
      gridType:    has ? gridType    : '',
      gridPattern: has ? (gridPat === 'None' ? '' : (gridPat || '')) : '',
      gridQtyV:    has ? qV : 0,
      gridQtyH:    has ? qH : 0,
      gridColor:   has ? insideGridColor : '',
      gridLenH:    has ? glassW - 1 : null,   // Grid Length H = glassW - 1
      gridLenV:    has ? glassH - 1 : null,   // Grid Length V = glassH - 1
      horizQty:    has ? qH * unitQty : 0,    // total horizontal bars to order
      vertQty:     has ? qV * unitQty : 0,    // total vertical bars to order
    };
  }

  const keeperGrid = parseGrid(KeeperGridType, KeeperGridPat, KeeperGridV, KeeperGridH, keeperGlassW, keeperGlassH);
  const lockGrid   = parseGrid(LockGridType,   LockGridPat,   LockGridV,   LockGridH,   lockGlassW,   lockGlassH);

  // ── Frame ─────────────────────────────────────────────────────────────────
  const frameHeightLen   = height + 2.25;
  const frameHeaderSillW = indivW + 0.25;

  const frameHeightPart = isWhite ? (withFin ? `5152-${color}` : `5452-${color}`) : `5152-${color}`;
  const frameHeaderPart = isWhite ? (withFin ? `5150-${color}` : `5450-${color}`) : `5150-${color}`;
  const frameSillPart   = isWhite ? (withFin ? `5191-${color}` : `5192-${color}`) : `5191-${color}`;

  // ── Sash ──────────────────────────────────────────────────────────────────
  const kSashH       = height * upperRatio - 1.4375;  // top (Keeper) sash — direct, was lSashH - 0.75
  const keeperRailW  = lSashW - 0.75;
  const lSashHPart   = lSashH + 0.25;
  const lockRailW    = lSashW + 0.25;

  const p5540 = `5540-${color}`;
  const p5541 = `5541-${color}`;
  const p5542 = `5542-${color}`;
  const p5543 = `5543-${color}`;

  // ── Reinforcements ────────────────────────────────────────────────────────
  const reinf665LockW = indivW - 5.75;
  const reinf665KepW  = indivW - 6.75;

  // ── Glazbead ──────────────────────────────────────────────────────────────
  const glazbeadPart = `5457-${glazbeadColor}`;
  const glazKH = kSashH - 3.0625 - 0.0625;  // -1/16 adjustment
  const glazLH = lSashH - 2.8125 - 0.0625;  // -1/16 adjustment
  const glazKW = indivW  - 6.25 + 0.0625;  // +1/16 adjustment
  const glazLW = indivW  - 5.25 + 0.0625;  // +1/16 adjustment

  // ── Misc ──────────────────────────────────────────────────────────────────
  const sillExtW      = indivW - 2.75;
  const balanceCoverL = height / 2 - 2;
  const snapMullL     = height - 0.125;

  const dripCapQty = (numLites === 1 || !withFin) ? 0 : qty;
  const dripCapLen = numLites === 1 ? null : overallW;
  const starterQty = withFin ? 0 : qty;

  const mullQtyMap  = { Single: 0, '2-Lite': 1, '3-Lite': 2, '4-Lite': 3, '5-Lite': 4, 'Q/H/Q': 2 };
  const snapMullQty = (mullQtyMap[config] || 0) * qty;

  // ── Balance ───────────────────────────────────────────────────────────────
  const balanceSize = lookupBalance(height, indivW, KeeperGlassPkg);
  const notch       = indivW <= 31.9375 ? "4'' Notch" : "7'' Notch";

  // ── Screen ────────────────────────────────────────────────────────────────
  const isFlexScrn = screenType === 'Flex Scrn';
  const isStdScrn  = screenType === 'Std Scrn';

  const flexScreen = isFlexScrn ? {
    qty:       unitQty,
    width:     indivW - 4.3125,
    height:    screenSize === 'Half Scrn' ? height / 2 - 0.625 : height - 3.125,
    meshType:  meshType || 'Plain',
    screenType, label,
  } : null;

  const stdScreenW           = indivW - 6.3125;
  const stdScreenH           = screenSize === 'Half Scrn' ? height / 2 - 2.25 : height - 5.0625;
  const stdScreenWithPullQty = isStdScrn ? (height < 50 ? unitQty : unitQty * 2) : 0;
  const stdScreenNoPullQty   = isStdScrn ? (height < 50 ? unitQty : 0) : 0;

  // ── Return ────────────────────────────────────────────────────────────────
  return {
    label, type: 'DH', color, overallW, width: overallW,
    height, config, qty, totalQty: unitQty, indivW, finType,
    screenType, screenSize, springH,

    // Spring balance section
    springBalance: [
      { label: 'Spring Height', qty: unitQty * 2, length: springH, part: '' },
    ],

    // Glass — each pane has full grid detail embedded
    glass: [
      {
        label:       'Keeper Glass',
        qty:         unitQty,
        width:       keeperGlassW,
        height:      keeperGlassH,
        glassType:   KeeperGlassPkg,
        glassConfig: KeeperGlassOpts !== 'None' ? KeeperGlassOpts : '',
        gasFill,
        // Grid fields — from parsed keeperGrid
        grids:       keeperGrid.hasGrids,
        gridType:    keeperGrid.gridType,
        gridPattern: keeperGrid.gridPattern,
        gridQtyV:    keeperGrid.gridQtyV,
        gridQtyH:    keeperGrid.gridQtyH,
        gridColor:   keeperGrid.gridColor,
        gridLenH:    keeperGrid.gridLenH,
        gridLenV:    keeperGrid.gridLenV,
        horizQty:    keeperGrid.horizQty,
        vertQty:     keeperGrid.vertQty,
      },
      {
        label:       'Lock Glass',
        qty:         unitQty,
        width:       lockGlassW,
        height:      lockGlassH,
        glassType:   LockGlassPkg,
        glassConfig: LockGlassOpts !== 'None' ? LockGlassOpts : '',
        gasFill,
        grids:       lockGrid.hasGrids,
        gridType:    lockGrid.gridType,
        gridPattern: lockGrid.gridPattern,
        gridQtyV:    lockGrid.gridQtyV,
        gridQtyH:    lockGrid.gridQtyH,
        gridColor:   lockGrid.gridColor,
        gridLenH:    lockGrid.gridLenH,
        gridLenV:    lockGrid.gridLenV,
        horizQty:    lockGrid.horizQty,
        vertQty:     lockGrid.vertQty,
      },
    ],

    frame: [
      { label: 'Frame Heights', qty: unitQty * 2, length: frameHeightLen,   part: frameHeightPart },
      { label: 'Frame Header',  qty: unitQty,     length: frameHeaderSillW, part: frameHeaderPart },
      { label: 'Frame Sill',    qty: unitQty,     length: frameHeaderSillW, part: frameSillPart   },
    ],

    sash: [
      { label: 'K Sash Heights',   qty: unitQty * 2, length: kSashH,      part: p5540 },
      { label: 'Keeper Rail',      qty: unitQty,     length: keeperRailW, part: p5543 },
      { label: 'Keeper Lift Rail', qty: unitQty,     length: keeperRailW, part: p5541 },
      { label: 'L Sash Heights',   qty: unitQty * 2, length: lSashHPart,  part: p5540 },
      { label: 'Lock Lift Rail',   qty: unitQty,     length: lockRailW,   part: p5541 },
      { label: 'Lock Rail',        qty: unitQty,     length: lockRailW,   part: p5542 },
    ],

    reinforcement: [
      { label: '665 Lock Reinf',   qty: unitQty, length: reinf665LockW, part: '665' },
      { label: '665 Keeper Reinf', qty: unitQty, length: reinf665KepW,  part: '665' },
    ],

    glazbead: [
      { label: '(x2) Glazbead K Heights', qty: unitQty * 2, length: glazKH, part: glazbeadPart },
      { label: '(x2) Glazbead L Heights', qty: unitQty * 2, length: glazLH, part: glazbeadPart },
      { label: '(x2) Glazbead K Widths',  qty: unitQty * 2, length: glazKW, part: glazbeadPart },
      { label: '(x2) Glazbead L Widths',  qty: unitQty * 2, length: glazLW, part: glazbeadPart },
    ],

    misc: [
      { label: '5131 Sill Extension', qty: unitQty,      length: sillExtW,      part: `5131-${color}` },
      ...(dripCapQty > 0 ? [
        { label: '5293 Drip Cap',     qty: dripCapQty,   length: dripCapLen,    part: isDark ? `5293-${glazbeadColor}` : `5293-${color}` },
      ] : []),
      { label: '(x2) Balance Cover',  qty: unitQty * 2,  length: balanceCoverL, part: `5104-${color}` },
      ...(starterQty > 0 ? [
        { label: 'Starter',           qty: starterQty,   length: overallW,      part: isDark ? `8351-${glazbeadColor}` : `8351-${color}` },
      ] : []),
      ...(snapMullQty > 0 ? [
        { label: '(x2) Snap Mull',    qty: snapMullQty,  length: snapMullL,     part: `8309-${color}` },
      ] : []),
    ],

    balance: { size: balanceSize, notch, qty: unitQty * 2 },

    flexScreen,
    stdScreen: isStdScrn ? {
      width: stdScreenW, height: stdScreenH,
      withPullQty: stdScreenWithPullQty, noPullQty: stdScreenNoPullQty,
      widthQty: unitQty * 2, screenColor: color, label,
    } : null,

    summary: {
      springH, sashWidth: lSashW,
      sashHeight: lSashH,   // backwards-compat (existing readers see bottom sash)
      keepSashH: kSashH,    // NEW: top sash height (Keeper)
      lockSashH: lSashH,    // NEW: bottom sash height (Lock)
      sashSplit,            // NEW: '50/50' | '25/75' | '75/25'
      keeperGlassW, keeperGlassH, lockGlassW, lockGlassH,
    },
  };
}